import jwt_decode from 'jwt-decode';
import * as types from '../constants/actionTypes';

const initialState = {
	id: 0,
	ehAdminRole: null,
	customerRole: null,
	accessToken: null,
	refreshToken: null,
	currentSubscriptionId: null,
	currentSubscriptionName: '',
	activeUserSubscriptions: null,
	adminUsers: [],
	loginWorkflow: null,
	redirectURL: null,
	userDisplayName: null,
	hasMultipleLocations: false,
	userKey: '',
	userProfilePicture: null,
	toolTips: [],
};

// Login comes back with all steps (if any exist) if there is only 1 subscription followed by the SEND_TO_DESTINATION step, otherwise it returns 1 step to CHOOSE_SUBSCRIPTION
// Once a subscription is selected it goes to get the steps specific for that sub (if any exist) followed by the SEND_TO_DESTINATION step
export default function accountReducer(state = initialState, action) {
	switch (action.type) {
		case types.LOGIN_SUCCESS:
		case types.MFA_LOGIN_SUCCESS: {
			const response = action.payload;
			let data = buildNewUserState(response, false);
			return Object.assign({}, state, data);
		}
		case types.POSTSSO_LOGIN_SUCCESS: {
			const response = action.payload;
			let data = buildNewUserState(response, true);
			return Object.assign({}, state, data);
		}
		case types.TOKEN_REFRESH_SUCCESS: {
			const response = action.payload;
			let data = {};
			if (response.accessToken && response.refreshToken) {
				data = getUser(response.accessToken, response.refreshToken);
			}
			return Object.assign({}, state, data);
		}
		case types.LOAD_ADMIN_USERS_SUCCESS: {
			const response = action.payload;
			let data = {};
			if (response.adminUsers) {
				data.adminUsers = response.adminUsers;
			}
			return Object.assign({}, state, data);
		}
		case types.SET_ACTIVE_USER_SUBSCRIPTIONS: {
			return {
				...state,
				activeUserSubscriptions: action.subscriptions,
			};
		}
		case types.SET_SSO_TEMP_TOKEN: {
			let data = {};
			if (action.token) {
				data = getUser(action.token, null);
			}
			return Object.assign({}, state, data);
		}
		case types.SET_CURRENT_SUBSCRIPTION: {
			return {
				...state,
				currentSubscriptionId: action.subscriptionId,
				currentSubscriptionName: action.subscriptionName,
				customerRole: action.customerRoleName,
				hasMultipleLocations: action.hasMultipleLocations,
			};
		}
		case types.SET_REDIRECT_URL: {
			if (!action.redirectURL) {
				return state;
			} else {
				return {
					...state,
					redirectURL: action.redirectURL,
				};
			}
		}
		case types.CLEAR_LOGIN_WORKFLOW: {
			return {
				...state,
				loginWorkflow: null,
			};
		}
		case types.COMPLETE_WORKFLOW_STEP: {
			let steps = state.loginWorkflow.steps;
			const indexToRemove = steps.findIndex((item) => item.name === action.name);
			// You can only complete steps in order, which means removing the
			// first entry
			if (indexToRemove !== 0) {
				handleError({ error: true, payload: { message: 'Attempting to remove out of order workflow step' } });
				break;
			}

			return {
				...state,
				loginWorkflow: {
					...state.loginWorkflow,
					steps: [...state.loginWorkflow.steps.slice(1)],
				},
			};
		}
		case types.GET_SUBSCRIPTION_DEPENDENT_WORKFLOW_STEPS_SUCCESS: {
			const existingSteps = state.loginWorkflow.steps.slice();
			const subscriptionDependentSteps = action.payload && action.payload.steps ? action.payload.steps.slice() : null;
			const newSteps =
				subscriptionDependentSteps && subscriptionDependentSteps.length > 0
					? existingSteps.concat(subscriptionDependentSteps)
					: existingSteps;

			return {
				...state,
				loginWorkflow: {
					...state.loginWorkflow,
					steps: [...newSteps],
				},
			};
		}

		case types.DISMISS_TOOL_TIP_REQUEST: {
			let tips = state.toolTips;
			const newTips = tips.filter((item) => item.id !== action.payload.toolTipId);

			return {
				...state,
				toolTips: [...newTips],
			};
		}

		case types.GET_SUBSCRIPTION_DEPENDENT_WORKFLOW_STEPS_FAILURE:
		case types.LOGIN_FAILURE:
		case types.FORGOT_PASSWORD_FAILURE:
		case types.FORGOT_USERNAME_FAILURE:
		case types.GET_USERNAME_FAILURE: {
			handleError(action);
			break;
		}

		case types.RESET_PASSWORD_REQUEST:
		case types.RESET_PASSWORD_SUCCESS:
		case types.RESET_PASSWORD_FAILURE:
		case types.GET_USERNAME_REQUEST:
		case types.GET_USERNAME_SUCCESS:
		case types.LOAD_ADMIN_USERS_REQUEST:
		case types.FORGOT_PASSWORD_REQUEST:
		case types.FORGOT_PASSWORD_SUCCESS:
		case types.LOGIN_REQUEST:
		case types.FORGOT_USERNAME_REQUEST:
		case types.FORGOT_USERNAME_SUCCESS:
		case types.TOKEN_REFRESH_REQUEST:
		case types.FREE_TRIAL_SIGN_UP_REQUEST:
		case types.FREE_TRIAL_SIGN_UP_SUCCESS:
		case types.FREE_TRIAL_SIGN_UP_FAILURE:
		case types.LOAD_FREE_TRIAL_SIGN_UP_FILTERS_REQUEST:
		case types.LOAD_FREE_TRIAL_SIGN_UP_FILTERS_SUCCESS:
		case types.LOAD_FREE_TRIAL_SIGN_UP_FILTERS_FAILURE:
		default:
			return state;
	}
}

const buildNewUserState = (response, isSSO) => {
	let data = {};

	if (response.accessToken && response.refreshToken) {
		data = getUser(response.accessToken, response.refreshToken);
	}
	if (response.subscriptionId && response.subscriptionId > 0) {
		data.currentSubscriptionId = response.subscriptionId;
	}
	if (response.subscriptionName) {
		data.currentSubscriptionName = response.subscriptionName;
	}
	if (response.customerRoleName) {
		data.customerRole = response.customerRoleName;
	}
	if (response.loginWorkflow) {
		data.loginWorkflow = response.loginWorkflow;
	}
	if (response.hasMultipleLocations) {
		data.hasMultipleLocations = response.hasMultipleLocations;
	}
	if (response.userDisplayName) {
		data.userDisplayName = response.userDisplayName;
	}
	if (response.userKey) {
		data.userKey = response.userKey;
	}
	if (isSSO === true) {
		data.isSSO = true;
	}
	if (response.userProfilePictureBase64) {
		data.userProfilePicture = response.userProfilePictureBase64;
	}
	if (response.toolTips) {
		data.toolTips = response.toolTips;
	}

	return data;
};

/**
 * All token interrogation will be isolated inside the userActions, and possibly
 * only inside the updateCurrentUser action creator, so this doesn't need to be
 * broken out into a helper module.  In fact, doing so would encourage its use
 * elsewhere, whereas keeping it a private function here discourages the
 * interrogation of access tokens elsewhere.
 * @param {*} accessToken The JWT access token
 * @param {*} refreshToken The refresh token used to get a new access token to
 *  extend the login session beyond the lifetime of the current access token
 */
const getUser = (accessToken, refreshToken) => {
	let user = initialState;
	let jwt = null;

	if (accessToken) {
		jwt = jwt_decode(accessToken);
	}

	if (!jwt) {
		return user; //empty
	}

	const current_time = Date.now().valueOf() / 1000;
	const isExpired = jwt && jwt.exp && jwt.exp < current_time;
	const isBeforeTokenIssuanceTime = jwt && jwt.nbf && jwt.nbf >= current_time;
	const ehAdminRole = jwt.role ? jwt.role : null;

	if (isExpired && isBeforeTokenIssuanceTime) {
		return user; //empty
	}

	user = {
		id: Number.parseInt(jwt.sub),
		ehAdminRole: ehAdminRole,
		accessToken: accessToken,
		refreshToken: refreshToken,
	};

	return user;
};

const handleError = (action) => {
	if (action && action.error && action.payload && action.payload.message) {
		throw action.payload.message;
	}
};
